import { useState, useEffect } from "react";
import { db, auth } from "../firebase/firebaseConfig";
import { collection, onSnapshot, query, doc, updateDoc, getDocs, where, increment, orderBy, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const TEAMS = ["CSK", "DC", "GT", "KKR", "LSG", "MI", "PBKS", "RCB", "RR", "SRH"];

function Admin() {
  const [matches, setMatches] = useState([]);
  const [iplTable, setIplTable] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [isUpdatingTable, setIsUpdatingTable] = useState(false);
  const [newMatch, setNewMatch] = useState({
    teamA: TEAMS[0],
    teamB: TEAMS[1],
    venue: "",
    date: "",
    time: "19:30"
  });
  const [isAddingMatch, setIsAddingMatch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let unsubTable = () => { };
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user || user.email !== "parthadmin@gmail.com") {
        navigate("/home");
      } else {
        // Fetch Admin's specific IPL Table from their own profile document
        unsubTable = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
          if (docSnap.exists() && docSnap.data().admin_ipl_table) {
            setIplTable(docSnap.data().admin_ipl_table);
          } else {
            const initialRanks = {};
            TEAMS.forEach((t, i) => initialRanks[t] = i + 1);
            setIplTable(initialRanks);
          }
          setLoading(false);
        }, (err) => {
          console.error("Table fetch failed:", err);
          setLoading(false);
        });
      }
    });

    const qMatches = query(collection(db, "matches"), orderBy("id", "asc"));
    const unsubMatches = onSnapshot(qMatches, (snapshot) => {
      setMatches(snapshot.docs.map(doc => ({ firebaseId: doc.id, ...doc.data() })));
    }, (err) => {
      console.error("Match fetch failed:", err);
      // Don't kill loading if matches fail, let auth handle it
    });

    return () => {
      unsubAuth();
      unsubMatches();
      unsubTable();
    };
  }, [navigate]);

  const updateTeamRank = (team, rank) => {
    setIplTable(prev => ({ ...prev, [team]: parseInt(rank) }));
  };

  const saveIplTable = async () => {
    setIsUpdatingTable(true);
    try {
      const user = auth.currentUser;
      if (user) {
        // Save the table directly inside the Admin's own UID to bypass all security rule blocks
        await setDoc(doc(db, "users", user.uid), { admin_ipl_table: iplTable }, { merge: true });
        alert("IPL Points Table Updated & Synced Securely! 🏟️");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update points table.");
    } finally {
      setIsUpdatingTable(false);
    }
  };

  const calculatePoints = (team) => {
    const rank = iplTable[team] || 1;
    // High-Stakes Wild Guess Formula
    if (rank >= 10) return 75;  // Ultimate Underdog Jackpot
    if (rank >= 8) return 50;   // High Risk
    if (rank >= 6) return 35;   // Challenging Pick
    if (rank >= 4) return 20;   // Mid-Table Pick
    return 10;                  // Safe Bet (Rank 1-3)
  };

  const declareResult = async (match, winnerTeam) => {
    const rewardPoints = calculatePoints(winnerTeam);
    if (!window.confirm(`Declare ${winnerTeam} as Winner? Reward for this pick: ${rewardPoints} PTS.`)) return;

    const teamAScore = window.prompt(`Enter ${match.teamA} Score (e.g., 180/4) [Optional]:`, "") || "";
    const teamBScore = window.prompt(`Enter ${match.teamB} Score (e.g., 185/3) [Optional]:`, "") || "";
    const resultReason = window.prompt(`Enter Match Result Summary (e.g., ${winnerTeam} won by 5 wkts):`, `${winnerTeam} won`) || `${winnerTeam} won`;

    setProcessingId(match.id);
    try {
      // 1. Update Match Status
      const matchRef = doc(db, "matches", match.firebaseId);
      await updateDoc(matchRef, {
        status: "finished",
        winner: winnerTeam,
        rewardPoints: rewardPoints, // Store what was awarded
        teamAScore: teamAScore,
        teamBScore: teamBScore,
        resultReason: resultReason
      });

      // 2. Fetch all predictions for this match
      const predQuery = query(collection(db, "match_predictions"), where("matchId", "==", parseInt(match.id)));
      const predSnap = await getDocs(predQuery);

      // 3. Process Points & Accuracy for each prediction
      const pointPromises = predSnap.docs.map(async (pDoc) => {
        const pick = pDoc.data();
        const userRef = doc(db, "users", pick.userId);

        if (pick.team === winnerTeam) {
          await updateDoc(userRef, {
            points: increment(rewardPoints),
            correctPredictions: increment(1)
          });
          await updateDoc(doc(db, "match_predictions", pDoc.id), {
            status: 'correct',
            pointsEarned: rewardPoints
          });
        } else {
          await updateDoc(doc(db, "match_predictions", pDoc.id), { status: 'incorrect', pointsEarned: 0 });
        }
      });

      await Promise.all(pointPromises);
      alert(`MATCH ${match.id} FINALIZED! ${winnerTeam} WON. Users awarded ${rewardPoints} PTS.`);
    } catch (err) {
      console.error(err);
      alert("Error finalizing result.");
    } finally {
      setProcessingId(null);
    }
  };

  const updateMatchWindow = async (match) => {
    const min = window.prompt("Enter total prediction window in minutes (e.g. 60 for 1 hour after start time):", match.predictWindow || 20);
    if (!min || isNaN(min)) return;
    try {
      setProcessingId(match.id);
      await updateDoc(doc(db, "matches", match.firebaseId), {
        predictWindow: parseInt(min)
      });
      alert(`Prediction window for Match ${match.id} updated to ${min} minutes!`);
    } catch (err) {
      console.error(err);
      alert("Failed to update prediction window.");
    } finally {
      setProcessingId(null);
    }
  };

  const cancelMatch = async (match) => {
    const refundPoints = 10;
    if (!window.confirm(`Declare Match ${match.id} as CANCELLED/ABANDONED? All users who made a prediction will get a standard ${refundPoints} PTS refund.`)) return;

    setProcessingId(match.id);
    try {
      const matchRef = doc(db, "matches", match.firebaseId);
      await updateDoc(matchRef, {
        status: "cancelled",
        winner: "CANCELLED",
        rewardPoints: refundPoints
      });

      const predQuery = query(collection(db, "match_predictions"), where("matchId", "==", parseInt(match.id)));
      const predSnap = await getDocs(predQuery);

      const pointPromises = predSnap.docs.map(async (pDoc) => {
        const pick = pDoc.data();
        const userRef = doc(db, "users", pick.userId);

        // Everyone who participated gets standard points
        await updateDoc(userRef, {
          points: increment(refundPoints)
        });
        await updateDoc(doc(db, "match_predictions", pDoc.id), {
          status: 'cancelled',
          pointsEarned: refundPoints
        });
      });

      await Promise.all(pointPromises);
      alert(`MATCH ${match.id} CANCELLED! All participants refunded ${refundPoints} PTS.`);
    } catch (err) {
      console.error(err);
      alert("Error cancelling match.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    if (!newMatch.date || !newMatch.venue) {
      alert("Please fill in all fields.");
      return;
    }
    if (newMatch.teamA === newMatch.teamB) {
      alert("Team A and Team B must be different!");
      return;
    }

    setIsAddingMatch(true);
    try {
      let maxId = 0;
      matches.forEach(m => {
        const mid = parseInt(m.id);
        if (mid > maxId) maxId = mid;
      });
      const nextId = maxId + 1;

      const matchData = {
        id: nextId,
        teamA: newMatch.teamA,
        teamB: newMatch.teamB,
        venue: newMatch.venue,
        date: newMatch.date,
        time: newMatch.time,
        status: "upcoming",
        winner: null
      };

      await setDoc(doc(db, "matches", nextId.toString()), matchData);
      alert(`Match ${nextId} added successfully!`);
      // Reset form (keep time but clear date/venue)
      setNewMatch(prev => ({ ...prev, venue: "", date: "" }));
    } catch (err) {
      console.error(err);
      alert("Failed to add match.");
    } finally {
      setIsAddingMatch(false);
    }
  };

  if (loading) return <div style={styles.loader}>Synchronizing Command Center...</div>;


  return (
    <div style={{ minHeight: "100vh", background: 'var(--stadium-gradient)', paddingBottom: '4rem' }}>
      <Navbar />
      <div className="app-container">

        {/* 📊 IPL POINTS TABLE MANAGER */}
        <section className="glass-morphism animate-fade-in" style={styles.sectionCard}>
          <h2 className="gradient-text" style={{ fontSize: '1.8rem' }}>IPL TABLE MANAGER</h2>
          <p style={styles.subtitle}>Set team ranks to adjust dynamic prediction rewards (Higher rank = More Points).</p>

          <div style={styles.tableGrid}>
            {TEAMS.map(team => (
              <div key={team} style={styles.teamRankInput}>
                <span style={styles.rankTeamLabel}>{team}</span>
                <input
                  type="number"
                  min="1" max="10"
                  value={iplTable[team] || ""}
                  onChange={(e) => updateTeamRank(team, e.target.value)}
                  style={styles.rankField}
                />
                <span style={styles.rewardPreview}>+{calculatePoints(team)} PTS</span>
              </div>
            ))}
          </div>
          <button onClick={saveIplTable} style={styles.saveTableBtn} disabled={isUpdatingTable}>
            {isUpdatingTable ? "SYNCING TABLE..." : "SAVE & SYNC POINTS TABLE"}
          </button>
        </section>

        {/* ➕ ADD NEW MATCH SECTION */}
        <section className="glass-morphism animate-fade-in" style={styles.sectionCard}>
          <h2 className="gradient-text" style={{ fontSize: '1.8rem' }}>CREATE NEW MATCH</h2>
          <p style={styles.subtitle}>Dynamically schedule a new match in the arena.</p>
          
          <form onSubmit={handleAddMatch} style={styles.addMatchForm}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Team A</label>
              <select style={styles.input} value={newMatch.teamA} onChange={(e) => setNewMatch({...newMatch, teamA: e.target.value})}>
                {TEAMS.map(t => <option key={`A-${t}`} value={t}>{t}</option>)}
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Team B</label>
              <select style={styles.input} value={newMatch.teamB} onChange={(e) => setNewMatch({...newMatch, teamB: e.target.value})}>
                {TEAMS.map(t => <option key={`B-${t}`} value={t}>{t}</option>)}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Venue</label>
              <input type="text" style={styles.input} value={newMatch.venue} onChange={(e) => setNewMatch({...newMatch, venue: e.target.value})} placeholder="e.g. Wankhede Stadium" />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Date</label>
              <input type="date" style={styles.input} value={newMatch.date} onChange={(e) => setNewMatch({...newMatch, date: e.target.value})} />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Time</label>
              <input type="time" style={styles.input} value={newMatch.time} onChange={(e) => setNewMatch({...newMatch, time: e.target.value})} />
            </div>
            
            <button type="submit" style={styles.saveTableBtn} disabled={isAddingMatch}>
              {isAddingMatch ? "CREATING MATCH..." : "CREATE MATCH"}
            </button>
          </form>
        </section>

        {/* 🏆 MATCH DECLARATION SECTION */}
        <header style={{ margin: '3rem 0 1.5rem', textAlign: 'center' }}>
          <h2 className="gradient-text" style={{ fontSize: '2.5rem' }}>DECLARE OFFICIAL RESULTS</h2>
        </header>

        <div style={styles.matchGrid}>
          {matches.map((m) => (
            <div key={m.id} className="glass-morphism animate-fade-in" style={{ ...styles.adminCard, opacity: m.status === 'finished' ? 0.6 : 1 }}>
              <div style={styles.matchInfo}>
                <span style={styles.matchId}>MATCH #{m.id}</span>
                <span style={{ ...styles.status, color: m.status === 'finished' ? '#ef4444' : '#10b981' }}>
                  {m.status.toUpperCase()}
                </span>
              </div>

              <h2 style={styles.matchTitle}>{m.teamA} VS {m.teamB}</h2>
              <div style={styles.rewardBadges}>
                <span style={styles.rewardBadge}>{m.teamA}: <b>+{calculatePoints(m.teamA)}</b></span>
                <span style={styles.rewardBadge}>{m.teamB}: <b>+{calculatePoints(m.teamB)}</b></span>
                {m.status !== 'finished' && m.status !== 'cancelled' && (
                  <button onClick={() => updateMatchWindow(m)} style={styles.windowBtn} title="Edit Prediction Window">
                    ⏱ {m.predictWindow || 20}m Window
                  </button>
                )}
              </div>

              {m.status !== 'finished' && m.status !== 'cancelled' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => declareResult(m, m.teamA)} style={styles.winBtn} disabled={processingId === m.id}>
                      {processingId === m.id ? "SYNC..." : `${m.teamA} WON`}
                    </button>
                    <button onClick={() => declareResult(m, m.teamB)} style={styles.winBtn} disabled={processingId === m.id}>
                      {processingId === m.id ? "SYNC..." : `${m.teamB} WON`}
                    </button>
                  </div>
                  <button
                    onClick={() => cancelMatch(m)}
                    style={{ ...styles.winBtn, background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                    disabled={processingId === m.id}
                  >
                    {processingId === m.id ? "SYNCING..." : `CANCEL MATCH / ABANDONED`}
                  </button>
                </div>
              ) : (
                <div style={styles.resultBadge}>FINAL RESULT: {m.winner} {m.status === 'cancelled' ? '' : 'WON'}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  sectionCard: { padding: '2rem', borderRadius: '32px', marginBottom: '2rem' },
  subtitle: { color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '2rem' },
  addMatchForm: { display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontSize: '0.8rem', fontWeight: '900', color: 'var(--primary)' },
  input: { padding: '1rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '12px', fontSize: '1rem', outline: 'none' },
  tableGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' },
  teamRankInput: { background: 'var(--bg-primary)', padding: '1rem', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '0.8rem', border: '1px solid var(--glass-border)' },
  rankTeamLabel: { fontWeight: '900', color: 'var(--primary)', width: '40px' },
  rankField: { width: '50px', background: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-primary)', textAlign: 'center', padding: '0.4rem', borderRadius: '8px', fontWeight: '800' },
  rewardPreview: { fontSize: '0.7rem', fontWeight: '800', color: '#10b981' },
  saveTableBtn: { width: '100%', padding: '1.2rem', background: 'var(--primary)', color: 'black', borderRadius: '16px', fontWeight: '900', letterSpacing: '0.05em' },
  matchGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' },
  adminCard: { padding: '2rem', borderRadius: '24px', display: 'flex', flexDirection: 'column', gap: '1rem' },
  matchInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  matchId: { fontSize: '0.7rem', fontWeight: '900', opacity: 0.5 },
  status: { fontSize: '0.7rem', fontWeight: '900' },
  matchTitle: { fontSize: '1.5rem', fontWeight: '900' },
  rewardBadges: { display: 'flex', gap: '0.5rem', marginTop: '-0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  rewardBadge: { fontSize: '0.65rem', padding: '0.2rem 0.6rem', borderRadius: '50px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)' },
  windowBtn: { fontSize: '0.65rem', padding: '0.2rem 0.6rem', borderRadius: '50px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', cursor: 'pointer', fontWeight: '900', transition: '0.2s' },
  actions: { display: 'flex', gap: '0.5rem', marginTop: '1rem' },
  winBtn: { flex: 1, padding: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)', borderRadius: '12px', fontWeight: '900', fontSize: '0.8rem' },
  resultBadge: { marginTop: '1rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', textAlign: 'center', borderRadius: '12px', fontWeight: '900', border: '1px solid rgba(239, 68, 68, 0.2)' },
  loader: { textAlign: 'center', padding: '12rem', color: 'var(--primary)', fontSize: '1.5rem', fontWeight: '900' }
};

export default Admin;